import { useEffect, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ProgressRing from '../components/ui-kit/ProgressRing';
import useThemeStore from '../stores/themeStore';
import { getStudentInsights } from '../lib/api';

const DIMENSION_LABELS = {
  communication: 'Communication',
  technical_knowledge: 'Technical Knowledge',
  problem_solving: 'Problem Solving',
  confidence: 'Confidence',
  clarity: 'Clarity',
  structure: 'Structure',
};
const DIMENSION_ORDER = Object.keys(DIMENSION_LABELS);

// Validated 8-hue categorical theme (see dataviz skill reference palette) —
// order is the CVD-safety mechanism, never reorder or recolor per-topic.
const TOPIC_COLORS = {
  light: ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834'],
  dark: ['#3987e5', '#199e70', '#c98500', '#008300', '#9085e9', '#e66767', '#d55181', '#d95926'],
};

const CHART_INK = {
  light: { primary: '#0b0b0b', secondary: '#52514e', muted: '#898781', grid: '#e1e0d9', axis: '#c3c2b7', surface: '#fcfcfb' },
  dark: { primary: '#ffffff', secondary: '#c3c2b7', muted: '#898781', grid: '#2c2c2a', axis: '#383835', surface: '#1a1a19' },
};

// Single-series line stays in BotView's own brand violet (no categorical
// adjacency to validate — only applies when distinguishing multiple hues).
const LINE_COLOR = { light: '#3D1F6E', dark: '#A78BFA' };

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function ChartTooltip({ active, payload, label, ink, suffix = '' }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs shadow-lg"
      style={{ background: ink.surface, borderColor: ink.grid, color: ink.primary }}
    >
      <p className="font-semibold mb-0.5">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: ink.secondary }}>
          {Math.round(p.value)}{suffix}
        </p>
      ))}
    </div>
  );
}

export default function ProgressPage() {
  const { theme } = useThemeStore();
  const ink = CHART_INK[theme] || CHART_INK.light;
  const topicColors = TOPIC_COLORS[theme] || TOPIC_COLORS.light;

  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStudentInsights();
        if (!cancelled) setInsights(data);
      } catch (e) {
        if (!cancelled) setError('Could not load your progress data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dimensions = insights?.dimensions || {};
  const history = insights?.history || [];
  const topicScores = insights?.topic_scores || {};

  const historyChartData = history.map((h) => ({
    date: formatDate(h.date),
    score: h.total_score,
    label: h.label,
  }));
  const topicChartData = Object.entries(topicScores).map(([topic, score]) => ({ topic, score }));

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-[#3D1F6E] dark:text-[#A78BFA] font-semibold mb-1">
          Analytics
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
          Your Progress
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Track your interview readiness over time
        </p>
      </div>

      {error && (
        <p className="text-sm text-red-500" data-testid="insights-error">{error}</p>
      )}

      {/* Skill dimension rings */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 items-start">
        {DIMENSION_ORDER.map((key) => (
          <Card key={key} className="bg-card border-border card-shadow">
            <CardHeader className="pb-1">
              <CardTitle className="text-[11px] font-heading uppercase tracking-[0.15em] text-[#3D1F6E] dark:text-[#A78BFA] font-semibold">
                {DIMENSION_LABELS[key]}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center py-3">
              <ProgressRing value={loading ? 0 : (dimensions[key] || 0)} size={88} strokeWidth={7} label="Score" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Score over time */}
      <Card className="bg-card border-border card-shadow" data-testid="score-history-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
            Score Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!loading && historyChartData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              Complete a few mock interviews to see your score trend here.
            </p>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historyChartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="scoreAreaFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={LINE_COLOR[theme] || LINE_COLOR.light} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={LINE_COLOR[theme] || LINE_COLOR.light} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={ink.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: ink.muted, fontSize: 11 }} axisLine={{ stroke: ink.axis }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: ink.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                  <Tooltip content={<ChartTooltip ink={ink} suffix=" pts" />} />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke={LINE_COLOR[theme] || LINE_COLOR.light}
                    strokeWidth={2}
                    fill="url(#scoreAreaFill)"
                    dot={{ r: 3, fill: LINE_COLOR[theme] || LINE_COLOR.light, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Topic breakdown */}
      <Card className="bg-card border-border card-shadow" data-testid="topic-breakdown-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
            Topic Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!loading && topicChartData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              Topic-by-topic scores will appear here after your first completed interview.
            </p>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topicChartData} margin={{ top: 20, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid stroke={ink.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="topic" tick={{ fill: ink.muted, fontSize: 11 }} axisLine={{ stroke: ink.axis }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: ink.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                  <Tooltip content={<ChartTooltip ink={ink} suffix=" pts" />} cursor={{ fill: ink.grid, opacity: 0.4 }} />
                  <Bar dataKey="score" radius={[4, 4, 0, 0]} maxBarSize={56}>
                    {topicChartData.map((entry, i) => (
                      <Cell key={entry.topic} fill={topicColors[i % topicColors.length]} />
                    ))}
                    <LabelList dataKey="score" position="top" formatter={(v) => Math.round(v)} fill={ink.secondary} fontSize={11} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
